import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Skeleton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { GraphicEq as GraphicEqIcon, Home as HomeIcon, NavigateNext as NavigateNextIcon } from '@mui/icons-material'

import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { ExerciseContentEditor } from '@/components/exercises/editors/ExerciseContentEditor'
import {
  PLACEMENT_CLOSED_SKILLS,
  PLACEMENT_LEVELS,
  PLACEMENT_SKILLS,
  placementBankApi,
  type PlacementBankItem,
  type PlacementBankSummaryRow,
  type PlacementLevel,
  type PlacementSkill,
} from '@/api/placementBank'
import { ROUTES } from '@/utils/constants'
import { displayErrorMessage, getErrorMessage } from '@/utils/error'

/** Норма активных позиций на связку «уровень + навык» — та же, что в сидере API. */
const MIN_ITEMS_PER_CELL = 8

/** Пауза перед запросом при наборе в поиске. */
const SEARCH_DEBOUNCE_MS = 350

type EditorForm = {
  title: string
  instructions: string
  level: PlacementLevel
  position: number
  isActive: boolean
  content: Record<string, unknown>
}

/** Форма из позиции: одна функция на открытие диалога и на обновление после сохранения. */
function formOf(item: PlacementBankItem): EditorForm {
  return {
    title: item.exercise.title,
    instructions: item.exercise.instructions ?? '',
    level: item.level,
    position: item.position,
    isActive: item.is_active,
    content: item.exercise.content,
  }
}

/**
 * Банк калиброванных заданий адаптивного теста уровня.
 *
 * Раздел вычитки: содержимое банка правит человек уровня B2+, потому что
 * ошибка в тесте уровня подрывает доверие ко всей платформе, а починить её
 * пересевом нельзя — сев не заходит в уже созданную позицию.
 *
 * Редактор содержимого здесь тот же, что и в конструкторе заданий учителя:
 * вторая копия восемнадцати редакторов однажды разошлась бы с первой, и
 * вычитанное в банке задание перестало бы совпадать с тем, что видит ученик.
 *
 * Диалог не закрывается сам после сохранения: единица работы здесь — вычитка
 * одной позиции, у которой после правки скрипта сбрасывается озвучка, и
 * захлопнутое окно уносило бы вместе с собой и причину, и кнопку «Озвучить».
 */
export function AdminPlacementBankPage() {
  const { t } = useTranslation()

  const [items, setItems] = useState<PlacementBankItem[]>([])
  const [summary, setSummary] = useState<PlacementBankSummaryRow[] | null>(null)
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [perPage, setPerPage] = useState(20)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [skill, setSkill] = useState<PlacementSkill | ''>('')
  const [level, setLevel] = useState<PlacementLevel | ''>('')
  const [onlyInactive, setOnlyInactive] = useState(false)
  const [search, setSearch] = useState('')
  const [searchQuery, setSearchQuery] = useState('')

  const [editing, setEditing] = useState<PlacementBankItem | null>(null)
  const [form, setForm] = useState<EditorForm | null>(null)
  const [saving, setSaving] = useState(false)
  const [voicing, setVoicing] = useState(false)
  const [saved, setSaved] = useState(false)
  const [dialogError, setDialogError] = useState<string | null>(null)

  // Счётчик запросов списка: ответ отменённого не должен ни рисовать данные,
  // ни гасить загрузку, ни оставлять свою ошибку над свежим списком
  const requestRef = useRef(0)

  // Поиск с задержкой: без неё название позиции в 25 символов даёт 25 запросов,
  // каждый с LIKE по заголовкам заданий
  useEffect(() => {
    const timer = setTimeout(() => setSearchQuery(search.trim()), SEARCH_DEBOUNCE_MS)

    return () => clearTimeout(timer)
  }, [search])

  const loadSummary = useCallback(() => {
    void placementBankApi
      .summary()
      .then(setSummary)
      // Сводка вспомогательная: её сбой не должен закрывать список позиций.
      // Но и предупреждение о дырах при этом не рисуется: сетка перебирается
      // жёстко, и на несостоявшейся сводке все пятнадцать связок выглядели бы
      // пустыми — паника на ровном месте
      .catch(() => undefined)
  }, [])

  const load = useCallback(() => {
    const request = ++requestRef.current

    setLoading(true)
    setError(null)

    void placementBankApi
      .list({
        page: page + 1,
        per_page: perPage,
        skill: skill === '' ? undefined : skill,
        level: level === '' ? undefined : level,
        is_active: onlyInactive ? false : undefined,
        q: searchQuery === '' ? undefined : searchQuery,
      })
      .then((result) => {
        if (request !== requestRef.current) return

        setItems(result.data)
        setTotal(result.meta?.total ?? result.data.length)
        setLoading(false)
      })
      .catch((err: unknown) => {
        if (request !== requestRef.current) return

        setError(displayErrorMessage(getErrorMessage(err), t('common.error')))
        setLoading(false)
      })
  }, [page, perPage, skill, level, onlyInactive, searchQuery, t])

  useEffect(() => load(), [load])

  useEffect(() => loadSummary(), [loadSummary])

  // Страница за пределами выдачи: позиции с неё выключили из другой сессии или
  // фильтр сузил список. MUI на таком page рисует пустой диапазон, поэтому
  // возвращаемся на последнюю существующую страницу сами
  useEffect(() => {
    if (loading || page === 0 || page * perPage < total) return

    setPage(total === 0 ? 0 : Math.ceil(total / perPage) - 1)
  }, [loading, page, perPage, total])

  /**
   * Связки, где активных позиций меньше нормы.
   *
   * Перебираем ожидаемую сетку, а не строки ответа: связка, в которой нет ни
   * одной позиции, из выдачи с группировкой просто выпадает — а это и есть
   * самая большая дыра, ради которой предупреждение написано. Та же сетка на
   * стороне API ({@see PlacementBankSeeder::levels()}), иначе два отчёта об
   * одном и том же расходятся.
   *
   * Пока сводка не загружена — предупреждения нет: на пустом ответе жёсткая
   * сетка показала бы все связки пустыми.
   */
  const gaps = useMemo(() => {
    if (summary === null) return []

    const byCell = new Map(summary.map((row) => [`${row.level}:${row.skill}`, row.active]))
    const result: { level: PlacementLevel; skill: PlacementSkill; active: number }[] = []

    for (const cellLevel of PLACEMENT_LEVELS) {
      for (const cellSkill of PLACEMENT_CLOSED_SKILLS) {
        const active = byCell.get(`${cellLevel}:${cellSkill}`) ?? 0

        if (active < MIN_ITEMS_PER_CELL) {
          result.push({ level: cellLevel, skill: cellSkill, active })
        }
      }
    }

    return result
  }, [summary])

  const busy = saving || voicing

  const isListening = editing?.exercise.type === 'listening'

  const audioReady =
    isListening && ((editing?.exercise.content.audio as { status?: string } | undefined)?.status ?? 'none') === 'ready'

  /** Хоть один фильтр включён: от этого зависит текст пустого списка. */
  const filtersApplied = skill !== '' || level !== '' || onlyInactive || searchQuery !== ''

  const openEditor = (item: PlacementBankItem) => {
    setEditing(item)
    setForm(formOf(item))
    setDialogError(null)
    setSaved(false)
  }

  const closeEditor = () => {
    if (busy) return

    // Флаги saving/voicing здесь не сбрасываются намеренно: до этой строки
    // управление доходит только когда оба уже false (см. гейт выше), а снимают
    // их finally в save() и voice().
    setEditing(null)
    setForm(null)
    setDialogError(null)
    setSaved(false)
  }

  /** Есть несохранённые правки: сравниваем форму с последним ответом сервера. */
  const dirty = useMemo(() => {
    if (editing === null || form === null) return false

    return JSON.stringify(form) !== JSON.stringify(formOf(editing))
  }, [editing, form])

  const applyUpdated = (updated: PlacementBankItem) => {
    setItems((current) => current.map((row) => (row.id === updated.id ? updated : row)))
    setEditing(updated)
    setForm(formOf(updated))
  }

  const save = async () => {
    if (editing === null || form === null) return

    setSaving(true)
    setDialogError(null)
    setSaved(false)
    try {
      const updated = await placementBankApi.update(editing.id, {
        title: form.title,
        instructions: form.instructions.trim() === '' ? null : form.instructions,
        level: form.level,
        position: form.position,
        is_active: form.isActive,
        content: form.content,
      })

      applyUpdated(updated)
      setSaved(true)
      // Уровень и активность могли увести позицию из-под фильтра, а сводку —
      // за черту нормы: и список, и предупреждение перечитываем
      load()
      loadSummary()
    } catch (err) {
      setDialogError(displayErrorMessage(getErrorMessage(err), t('common.error')))
    } finally {
      setSaving(false)
    }
  }

  const voice = async () => {
    if (editing === null) return

    setVoicing(true)
    setDialogError(null)
    setSaved(false)
    try {
      // Сервер озвучивает скрипт из базы, а не из формы, поэтому кнопка
      // заблокирована при несохранённых правках (см. voiceDisabledReason)
      applyUpdated(await placementBankApi.voice(editing.id))
      load()
    } catch (err) {
      setDialogError(displayErrorMessage(getErrorMessage(err), t('common.error')))
    } finally {
      setVoicing(false)
    }
  }

  /**
   * Почему «Озвучить» недоступна; null — доступна.
   *
   * У готовой записи кнопка гаснет намеренно: сервер идемпотентен и повторный
   * запрос к платному провайдеру не отправляет, так что «переозвучить» было бы
   * ярлыком без действия. Переозвучка идёт через правку скрипта — она сбрасывает
   * запись и возвращает кнопку.
   */
  const voiceDisabledReason = dirty
    ? t('admin.placementBank.voiceSaveFirst')
    : audioReady
      ? t('admin.placementBank.voiceAlreadyDone')
      : null

  return (
    <DashboardLayout>
      <Box sx={{ p: 3 }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
          <Link to={ROUTES.DASHBOARD} style={{ display: 'flex', alignItems: 'center' }}>
            <HomeIcon fontSize="small" />
          </Link>
          <Typography color="text.primary">{t('admin.placementBank.title')}</Typography>
        </Breadcrumbs>

        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" component="h1" fontWeight={700}>
              {t('admin.placementBank.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('admin.placementBank.subtitle')}
            </Typography>
          </Box>
          <PageHelpPopover pageKey="admin-placement-bank" />
        </Box>

        {gaps.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('admin.placementBank.coverageGap', { count: gaps.length, min: MIN_ITEMS_PER_CELL })}
            <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
              {gaps
                .map((row) => `${row.level} · ${t(`admin.placementBank.skills.${row.skill}`)} — ${row.active}`)
                .join('; ')}
            </Typography>
          </Alert>
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }} alignItems={{ sm: 'center' }}>
          <TextField
            select
            size="small"
            label={t('admin.placementBank.skill')}
            value={skill}
            onChange={(event) => {
              setSkill(event.target.value as PlacementSkill | '')
              setPage(0)
            }}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            {PLACEMENT_SKILLS.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`admin.placementBank.skills.${value}`)}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label={t('admin.placementBank.level')}
            value={level}
            onChange={(event) => {
              setLevel(event.target.value as PlacementLevel | '')
              setPage(0)
            }}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            {PLACEMENT_LEVELS.map((value) => (
              <MenuItem key={value} value={value}>
                {value}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            size="small"
            label={t('admin.placementBank.search')}
            value={search}
            onChange={(event) => {
              setSearch(event.target.value)
              setPage(0)
            }}
            sx={{ minWidth: 220 }}
          />

          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={onlyInactive}
                onChange={(event) => {
                  setOnlyInactive(event.target.checked)
                  setPage(0)
                }}
              />
            }
            label={t('admin.placementBank.onlyInactive')}
          />
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading && (
          <Card>
            {/* Скелетон повторяет раскладку таблицы: полоса на всю высоту
                прыгала бы при подстановке строк */}
            <Box sx={{ p: 2 }}>
              <Skeleton variant="rounded" height={40} sx={{ mb: 1 }} />
              {Array.from({ length: 8 }).map((_, index) => (
                <Skeleton key={index} variant="rounded" height={52} sx={{ mb: 1 }} />
              ))}
            </Box>
          </Card>
        )}

        {!loading && items.length === 0 && (
          <Card sx={{ p: 3 }}>
            {/* Три разных пустоты: страница за концом выдачи, отсеявший всё
                фильтр и по-настоящему незасеянный банк. Совет «засейте банк»
                под включённым фильтром был бы прямой неправдой */}
            <Typography color="text.secondary">
              {page > 0
                ? t('admin.placementBank.emptyPage')
                : filtersApplied
                  ? t('admin.placementBank.emptyFiltered')
                  : t('admin.placementBank.empty')}
            </Typography>
          </Card>
        )}

        {!loading && items.length > 0 && (
          <TableContainer component={Card} sx={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('admin.placementBank.exercise')}</TableCell>
                  <TableCell>{t('admin.placementBank.level')}</TableCell>
                  <TableCell>{t('admin.placementBank.skill')}</TableCell>
                  <TableCell>{t('admin.placementBank.type')}</TableCell>
                  <TableCell>{t('admin.placementBank.state')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow
                    key={item.id}
                    hover
                    tabIndex={0}
                    role="button"
                    aria-label={item.exercise.title}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => openEditor(item)}
                    onKeyDown={(event) => {
                      // Строка открывается и с клавиатуры: раздел вычитки
                      // проходят подряд, и мышь на каждую позицию — лишний шаг
                      if (event.key !== 'Enter' && event.key !== ' ') return

                      event.preventDefault()
                      openEditor(item)
                    }}
                  >
                    <TableCell>{item.exercise.title}</TableCell>
                    <TableCell>{item.level}</TableCell>
                    <TableCell>{t(`admin.placementBank.skills.${item.skill}`)}</TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {t(`exercises.types.${item.exercise.type}.name`)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        {!item.is_active && (
                          <Chip size="small" color="default" label={t('admin.placementBank.inactive')} />
                        )}
                        {item.exercise.status === 'draft' && (
                          <Tooltip title={t('admin.placementBank.draftHint')}>
                            <Chip size="small" color="warning" label={t('admin.placementBank.draft')} />
                          </Tooltip>
                        )}
                        {item.is_probe && <Chip size="small" color="info" label={t('admin.placementBank.probe')} />}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Пагинация вне ветки с данными: перелистывание уводит список в
            загрузку, и внутри ветки контрол исчезал бы вместе с ним. На пустом
            банке она не рисуется — «0–0 из 0» под словами «банк пуст» ничего
            не сообщает */}
        {total > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, overflow: 'hidden', minWidth: 0 }}>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={perPage}
              onRowsPerPageChange={(event) => {
                setPerPage(parseInt(event.target.value, 10))
                setPage(0)
              }}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage={t('admin.placementBank.rowsPerPage')}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}–${to} ${t('common.of')} ${count === -1 ? `${to}+` : count}`
              }
              // Без переноса тулбар не помещается в 360 px и уезжает под
              // overflow: hidden вместе со стрелками перелистывания
              sx={{ '& .MuiTablePagination-toolbar': { flexWrap: 'wrap', rowGap: 1 } }}
            />
          </Box>
        )}

        <Dialog
          open={editing !== null}
          // Закрывает только явная кнопка: клик мимо окна и Escape уносили бы
          // вычитку задания на полтора десятка полей без единого вопроса
          onClose={(_, reason) => {
            if (reason === 'backdropClick' || reason === 'escapeKeyDown') return

            closeEditor()
          }}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>{t('admin.placementBank.editTitle')}</DialogTitle>
          <DialogContent dividers>
            {editing !== null && form !== null && (
              <Stack spacing={2} sx={{ pt: 1 }}>
                {dialogError && (
                  <Alert severity="error" onClose={() => setDialogError(null)}>
                    {dialogError}
                  </Alert>
                )}

                {saved && !dirty && <Alert severity="success">{t('admin.placementBank.saved')}</Alert>}

                {isListening && !audioReady && <Alert severity="warning">{t('admin.placementBank.audioMissing')}</Alert>}

                <TextField
                  fullWidth
                  size="small"
                  label={t('admin.placementBank.exercise')}
                  value={form.title}
                  disabled={busy}
                  onChange={(event) =>
                    setForm((current) => (current === null ? current : { ...current, title: event.target.value }))
                  }
                />

                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  size="small"
                  label={t('admin.placementBank.instructions')}
                  value={form.instructions}
                  disabled={busy}
                  onChange={(event) =>
                    setForm((current) => (current === null ? current : { ...current, instructions: event.target.value }))
                  }
                />

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    select
                    size="small"
                    label={t('admin.placementBank.level')}
                    helperText={t('admin.placementBank.levelHint')}
                    value={form.level}
                    disabled={busy}
                    onChange={(event) =>
                      setForm((current) =>
                        current === null ? current : { ...current, level: event.target.value as PlacementLevel },
                      )
                    }
                    sx={{ minWidth: 180 }}
                  >
                    {PLACEMENT_LEVELS.map((value) => (
                      <MenuItem key={value} value={value}>
                        {value}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    size="small"
                    type="number"
                    label={t('admin.placementBank.position')}
                    value={form.position}
                    disabled={busy}
                    slotProps={{ htmlInput: { min: 0, max: 9999, step: 1 } }}
                    onChange={(event) => {
                      // Пустое поле и мусор дают NaN: он уходит в JSON как null
                      // и возвращается общим 422 вместо понятного отказа
                      const next = Number.parseInt(event.target.value, 10)

                      setForm((current) =>
                        current === null
                          ? current
                          : { ...current, position: Number.isFinite(next) ? Math.min(Math.max(next, 0), 9999) : 0 },
                      )
                    }}
                    sx={{ minWidth: 120 }}
                  />

                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.isActive}
                        disabled={busy}
                        onChange={(event) =>
                          setForm((current) =>
                            current === null ? current : { ...current, isActive: event.target.checked },
                          )
                        }
                      />
                    }
                    label={t('admin.placementBank.active')}
                  />
                </Stack>

                <Divider />

                {/* Редактор содержимого — тот же, что у заданий учителя:
                    вычитка правит английский там, где он показывается ученику */}
                <ExerciseContentEditor
                  type={editing.exercise.type}
                  content={form.content}
                  onChange={(content) => setForm((current) => (current === null ? current : { ...current, content }))}
                  disabled={busy}
                  systemTts
                />

                {editing.is_probe && editing.evaluation_criteria !== null && (
                  <>
                    <Divider />
                    <Typography variant="subtitle2">{t('admin.placementBank.criteria')}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('admin.placementBank.criteriaHint')}
                    </Typography>
                    {Object.entries(editing.evaluation_criteria).map(([key, value]) => (
                      <Box key={key}>
                        <Typography variant="caption" fontWeight={600}>
                          {key}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {value}
                        </Typography>
                      </Box>
                    ))}
                  </>
                )}
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            {isListening && (
              <Tooltip title={voiceDisabledReason ?? t('admin.placementBank.voiceHint')}>
                <span>
                  <Button
                    startIcon={<GraphicEqIcon />}
                    disabled={busy || voiceDisabledReason !== null}
                    onClick={() => void voice()}
                  >
                    {voicing ? t('common.loading') : t('admin.placementBank.voice')}
                  </Button>
                </span>
              </Tooltip>
            )}
            <Box sx={{ flex: 1 }} />
            <Button onClick={closeEditor} disabled={busy}>
              {t('common.close')}
            </Button>
            <Button
              variant="contained"
              disabled={busy || form === null || form.title.trim() === '' || !dirty}
              onClick={() => void save()}
            >
              {saving ? t('common.loading') : t('common.save')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </DashboardLayout>
  )
}
